import { z } from "zod";

import { eventSchema } from "../shared";

// User Registration Events
export const userRegisteredEvent = eventSchema.extend({
  event: z.string().default("user_registered"),
  properties: z.object({
    provider: z.enum(["email", "github", "google", "linkedin", "openid"]),
    isAnonymousConversion: z.boolean().default(false),
    locale: z.string().optional(),
  }),
});

export const anonymousUserCreatedEvent = eventSchema.extend({
  event: z.string().default("anonymous_user_created"),
  properties: z.object({
    fingerprint: z.string(),
  }),
});

// User Login Events
export const userLoginEvent = eventSchema.extend({
  event: z.string().default("user_login"),
  properties: z.object({
    provider: z.enum(["email", "github", "google", "linkedin", "openid", "anonymous"]),
    isAnonymousMerge: z.boolean().default(false),
    twoFactorRequired: z.boolean().default(false),
  }),
});

export const userLogoutEvent = eventSchema.extend({
  event: z.string().default("user_logout"),
  properties: z.object({}),
});

// Password Management Events
export const passwordResetRequestedEvent = eventSchema.extend({
  event: z.string().default("password_reset_requested"),
  properties: z.object({
    email: z.string(),
  }),
});

export const passwordResetCompletedEvent = eventSchema.extend({
  event: z.string().default("password_reset_completed"),
  properties: z.object({
    email: z.string(),
  }),
});

export const passwordUpdatedEvent = eventSchema.extend({
  event: z.string().default("password_updated"),
  properties: z.object({}),
});

// Two-Factor Authentication Events
export const twoFactorSetupEvent = eventSchema.extend({
  event: z.string().default("two_factor_setup"),
  properties: z.object({}),
});

export const twoFactorEnabledEvent = eventSchema.extend({
  event: z.string().default("two_factor_enabled"),
  properties: z.object({}),
});

export const twoFactorDisabledEvent = eventSchema.extend({
  event: z.string().default("two_factor_disabled"),
  properties: z.object({}),
});

export const twoFactorVerifiedEvent = eventSchema.extend({
  event: z.string().default("two_factor_verified"),
  properties: z.object({
    method: z.enum(["code", "backup"]),
  }),
});

// Email Verification Events
export const emailVerificationSentEvent = eventSchema.extend({
  event: z.string().default("email_verification_sent"),
  properties: z.object({
    email: z.string(),
  }),
});

export const emailVerifiedEvent = eventSchema.extend({
  event: z.string().default("email_verified"),
  properties: z.object({
    email: z.string(),
  }),
});

// OAuth Events
export const oauthLoginStartedEvent = eventSchema.extend({
  event: z.string().default("oauth_login_started"),
  properties: z.object({
    provider: z.enum(["github", "google", "linkedin", "openid"]),
  }),
});

export const oauthLoginCompletedEvent = eventSchema.extend({
  event: z.string().default("oauth_login_completed"),
  properties: z.object({
    provider: z.enum(["github", "google", "linkedin", "openid"]),
    isNewUser: z.boolean(),
    isAnonymousMerge: z.boolean().default(false),
  }),
});

// Anonymous User Merge Events
export const anonymousUserMergedEvent = eventSchema.extend({
  event: z.string().default("anonymous_user_merged"),
  properties: z.object({
    anonymousUserId: z.string(),
    targetUserId: z.string(),
    mergeType: z.enum(["registration", "login", "oauth"]),
  }),
});

// Type exports
export type UserRegisteredEvent = z.infer<typeof userRegisteredEvent>;
export type AnonymousUserCreatedEvent = z.infer<typeof anonymousUserCreatedEvent>;
export type UserLoginEvent = z.infer<typeof userLoginEvent>;
export type UserLogoutEvent = z.infer<typeof userLogoutEvent>;
export type PasswordResetRequestedEvent = z.infer<typeof passwordResetRequestedEvent>;
export type PasswordResetCompletedEvent = z.infer<typeof passwordResetCompletedEvent>;
export type PasswordUpdatedEvent = z.infer<typeof passwordUpdatedEvent>;
export type TwoFactorSetupEvent = z.infer<typeof twoFactorSetupEvent>;
export type TwoFactorEnabledEvent = z.infer<typeof twoFactorEnabledEvent>;
export type TwoFactorDisabledEvent = z.infer<typeof twoFactorDisabledEvent>;
export type TwoFactorVerifiedEvent = z.infer<typeof twoFactorVerifiedEvent>;
export type EmailVerificationSentEvent = z.infer<typeof emailVerificationSentEvent>;
export type EmailVerifiedEvent = z.infer<typeof emailVerifiedEvent>;
export type OAuthLoginStartedEvent = z.infer<typeof oauthLoginStartedEvent>;
export type OAuthLoginCompletedEvent = z.infer<typeof oauthLoginCompletedEvent>;
export type AnonymousUserMergedEvent = z.infer<typeof anonymousUserMergedEvent>;
